import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { Modal, Field, TextInput, FormActions } from '../components/form';
import { ToolForm, CategoryForm, LocationForm, type ToolRow, type CategoryRow, type LocationRow } from '../components/InventoryForms';

type Tool = ToolRow & {
  inventory_categories: { name: string } | null;
  inventory_locations: { name: string } | null;
  suppliers: { name: string } | null;
};
type Alert = { tool_id: string; name: string; kind: string; due_on: string };

const STATUS_TONE: Record<string, 'inprogress' | 'open' | 'overdue' | 'onhold' | 'done'> = {
  available: 'done', in_use: 'inprogress', maintenance: 'onhold', retired: 'onhold', lost: 'overdue',
};

export function Tools() {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency, role } = useOrg();
  const canWrite = ['org_admin', 'manager', 'superadmin'].includes(role);
  const [tools, setTools] = useState<Tool[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [toolForm, setToolForm] = useState(false);
  const [editTool, setEditTool] = useState<ToolRow | null>(null);
  const [library, setLibrary] = useState(false);
  const [calTool, setCalTool] = useState<Tool | null>(null);

  const load = useCallback(async () => {
    const [tl, al] = await Promise.all([
      supabase.from('tools').select('id,name,serial_number,category_id,location_id,supplier_id,purchase_date,purchase_price,currency,warranty_until,next_calibration_at,status,notes,qr_token,inventory_categories(name),inventory_locations(name),suppliers(name)').is('deleted_at', null).order('name'),
      supabase.rpc('tool_alerts', { p_org: orgId }),
    ]);
    setTools((tl.data as unknown as Tool[]) ?? []);
    setAlerts((al.data as Alert[]) ?? []);
    setLoading(false);
  }, [orgId]);
  useEffect(() => { if (orgId) void load(); }, [orgId, load]);

  const dueSet = useMemo(() => new Set(alerts.map((a) => a.tool_id)), [alerts]);

  async function checkOut(id: string) { await supabase.rpc('tool_check_out', { p_tool: id }); await load(); }
  async function checkIn(id: string) { await supabase.rpc('tool_check_in', { p_tool: id }); await load(); }

  return (
    <Layout header={<Topbar actions={canWrite ? <>
      <Button variant="secondary" onClick={() => setLibrary(true)}>{t('inv.library')}</Button>
      <Button onClick={() => { setEditTool(null); setToolForm(true); }}>+ {t('tool.new')}</Button>
    </> : undefined} />}>
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.tools')}</h1>
        <section className="grid grid-cols-2 gap-3.5 sm:grid-cols-4">
          <Card className="flex flex-col gap-1 p-[18px]" style={{ background: 'var(--pv-chrome)', color: '#fff' }}><span className="text-xs text-chrome-muted">{t('tool.count')}</span><span className="font-mono text-[22px] font-medium">{tools.length}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('tool.inUse')}</span><span className="font-mono text-[22px] font-medium">{tools.filter((x) => x.status === 'in_use').length}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('tool.calibrationDue')}</span><span className={cn('font-mono text-[22px] font-medium', alerts.some((a) => a.kind === 'calibration') && 'text-st-overdue-fg')}>{alerts.filter((a) => a.kind === 'calibration').length}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('tool.warrantyEnding')}</span><span className={cn('font-mono text-[22px] font-medium', alerts.some((a) => a.kind === 'warranty') && 'text-st-overdue-fg')}>{alerts.filter((a) => a.kind === 'warranty').length}</span></Card>
        </section>
      </div>

      {alerts.length > 0 && (
        <div className="px-7 pt-[18px]">
          <Card className="flex flex-col gap-1.5 border-l-2 border-st-overdue-fg p-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-st-overdue-fg">{t('tool.alerts')}</span>
            {alerts.slice(0, 8).map((a, i) => (
              <span key={i} className="text-sm"><span className="font-medium">{a.name}</span> — {t(`tool.alert_${a.kind}`)} · {a.due_on}</span>
            ))}
          </Card>
        </div>
      )}

      <div className="flex flex-col gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr className="text-left text-xs text-muted">
                <th className="px-5 py-3 font-medium">{t('form.name')}</th>
                <th className="px-2 py-3 font-medium">{t('tool.serial')}</th>
                <th className="px-2 py-3 font-medium">{t('inv.category')}</th>
                <th className="px-2 py-3 font-medium">{t('tool.status')}</th>
                <th className="px-2 py-3 font-medium">{t('tool.nextCalibration')}</th>
                <th className="px-2 py-3 font-medium">{t('tool.purchasePrice')}</th>
                <th className="px-5 py-3" />
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                  : tools.length === 0 ? <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('tool.none')}</td></tr>
                  : tools.map((tl) => (
                    <tr key={tl.id} className="border-t border-divider hover:bg-bg">
                      <td className="px-5 py-3.5 font-medium">{tl.name}</td>
                      <td className="px-2 py-3.5 font-mono text-xs text-muted">{tl.serial_number ?? '—'}</td>
                      <td className="px-2 py-3.5 text-muted">{tl.inventory_categories?.name ?? '—'}</td>
                      <td className="px-2 py-3.5"><StatusPill tone={STATUS_TONE[tl.status] ?? 'onhold'}>{t(`tool.status_${tl.status}`)}</StatusPill></td>
                      <td className={cn('px-2 py-3.5 font-mono text-xs', dueSet.has(tl.id) ? 'text-st-overdue-fg' : 'text-muted')}>{tl.next_calibration_at ?? '—'}</td>
                      <td className="px-2 py-3.5 font-mono text-muted">{formatCurrency(tl.purchase_price, tl.currency, i18n.language)}</td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex justify-end gap-1">
                          {canWrite && tl.status === 'available' && <Button variant="ghost" size="sm" onClick={() => checkOut(tl.id)}>{t('tool.checkOut')}</Button>}
                          {canWrite && tl.status === 'in_use' && <Button variant="ghost" size="sm" onClick={() => checkIn(tl.id)}>{t('tool.checkIn')}</Button>}
                          {canWrite && <Button variant="ghost" size="sm" onClick={() => setCalTool(tl)}>{t('tool.calibration')}</Button>}
                          {canWrite && <Button variant="ghost" size="sm" onClick={() => { setEditTool(tl); setToolForm(true); }}>{t('form.edit')}</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ToolForm open={toolForm} onClose={() => setToolForm(false)} onSaved={load} initial={editTool} />
      {library && <LibraryManager onClose={() => { setLibrary(false); void load(); }} />}
      {calTool && <CalibrationModal tool={calTool} onClose={() => setCalTool(null)} onSaved={() => { setCalTool(null); void load(); }} />}
    </Layout>
  );
}

// Log a calibration (sets next calibration date + writes a tool_movement)
function CalibrationModal({ tool, onClose, onSaved }: { tool: Tool; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [next, setNext] = useState(tool.next_calibration_at ?? '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!next) { setErr(t('form.errRequired')); return; }
    setBusy(true);
    const { error } = await supabase.rpc('tool_log_calibration', { p_tool: tool.id, p_next: next, p_notes: notes.trim() || null });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  }
  return (
    <Modal open onClose={onClose} title={`${t('tool.calibration')} — ${tool.name}`}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <Field label={t('tool.nextCalibration')} required><TextInput type="date" value={next} onChange={(e) => setNext(e.target.value)} autoFocus /></Field>
        <Field label={t('form.notes')}><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// Parametrable library manager: tool categories + locations
function LibraryManager({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [cats, setCats] = useState<CategoryRow[]>([]);
  const [locs, setLocs] = useState<LocationRow[]>([]);
  const [catForm, setCatForm] = useState<CategoryRow | null | 'new'>(null);
  const [locForm, setLocForm] = useState<LocationRow | null | 'new'>(null);
  const load = useCallback(async () => {
    const [c, l] = await Promise.all([
      supabase.from('inventory_categories').select('id,kind,code,name,parent_id').eq('kind', 'tool').is('deleted_at', null).order('name'),
      supabase.from('inventory_locations').select('id,code,name,notes').is('deleted_at', null).order('name'),
    ]);
    setCats((c.data as any) ?? []); setLocs((l.data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);
  return (
    <Modal open onClose={onClose} title={t('inv.library')} wide>
      <div className="grid grid-cols-2 gap-5 p-5">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between"><span className="eyebrow">{t('inv.categories')}</span><Button size="sm" variant="secondary" onClick={() => setCatForm('new')}>+ {t('inv.newCategory')}</Button></div>
          {cats.map((c) => <button key={c.id} onClick={() => setCatForm(c)} className="flex justify-between rounded-md border border-divider px-3 py-2 text-left text-sm hover:bg-bg"><span>{c.name}</span><span className="font-mono text-2xs text-muted">{c.code}</span></button>)}
          {cats.length === 0 && <span className="text-2xs text-muted">{t('common.noData')}</span>}
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between"><span className="eyebrow">{t('inv.locations')}</span><Button size="sm" variant="secondary" onClick={() => setLocForm('new')}>+ {t('inv.newLocation')}</Button></div>
          {locs.map((l) => <button key={l.id} onClick={() => setLocForm(l)} className="flex justify-between rounded-md border border-divider px-3 py-2 text-left text-sm hover:bg-bg"><span>{l.name}</span><span className="font-mono text-2xs text-muted">{l.code}</span></button>)}
          {locs.length === 0 && <span className="text-2xs text-muted">{t('common.noData')}</span>}
        </div>
      </div>
      {catForm && <CategoryForm open onClose={() => setCatForm(null)} onSaved={load} initial={catForm === 'new' ? null : catForm} kind="tool" />}
      {locForm && <LocationForm open onClose={() => setLocForm(null)} onSaved={load} initial={locForm === 'new' ? null : locForm} />}
    </Modal>
  );
}
